import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, MailCheck, MoreHorizontal, ShieldCheck, ShieldOff, UserPlus } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import { ROLE_LABEL, type Program, type Role } from '@/lib/types';
import { cn, fmtDate, fmtRelative, initials } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox, Field, Input, Select } from '@/components/ui/field';
import { Alert, Badge, Card, CardHeader, Spinner } from '@/components/ui/misc';
import { Dialog } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';

interface Member {
  id: string;
  userId: string;
  name: string;
  email: string;
  role: Role;
  title: string | null;
  seat: string | null;
  budgetAccess: boolean;
  mfaEnabled: boolean;
  lastLoginAt: string | null;
  disabled: boolean;
  isSelf: boolean;
}
interface Invitation {
  id: string;
  email: string;
  role: Role;
  title: string | null;
  expiresAt: string;
  createdAt: string;
}
interface Seat {
  id: string;
  title: string;
  holderName: string | null;
  userId: string | null;
}

const ISSUABLE: Role[] = ['ADMIN', 'EXECUTIVE', 'CONTRIBUTOR'];
const ROLE_HINT: Record<string, string> = {
  ADMIN: 'Owns the plan, publishes reports, manages users. MFA required.',
  EXECUTIVE: 'Steering Committee. Reads everything, records decisions, accepts when designated.',
  CONTRIBUTOR: 'Project team and SMEs. Updates own tasks, uploads documents, raises RAID items.',
};

export function People({ program }: { program: Program }) {
  const base = `/api/programs/${program.id}/admin`;
  const qc = useQueryClient();
  const toast = useToast();
  const [inviting, setInviting] = useState(false);
  const [link, setLink] = useState<{ email: string; url: string } | null>(null);

  const q = useQuery({
    queryKey: ['admin', program.id, 'members'],
    queryFn: () => api.get<{ members: Member[]; invitations: Invitation[] }>(`${base}/members`),
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['admin', program.id] });

  const act = useMutation({
    mutationFn: ({ url, body, method = 'post' }: { url: string; body?: unknown; method?: 'post' | 'patch' }) =>
      api[method](url, body),
    onSuccess: () => refresh(),
    onError: (e) => toast.error(errorMessage(e)),
  });

  async function resend(inv: Invitation) {
    try {
      const res = await api.post<{ emailed: boolean; inviteUrl?: string }>(`${base}/invitations/${inv.id}/resend`);
      if (res.inviteUrl) setLink({ email: inv.email, url: res.inviteUrl });
      else toast.ok(`Invitation emailed to ${inv.email}.`);
      refresh();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }

  if (q.isLoading) return <Spinner />;
  const { members, invitations } = q.data!;

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <CardHeader
          title={`Members (${members.length})`}
          description="Access is by invitation only. Roles are enforced by the server on every request."
          actions={
            <Button size="sm" onClick={() => setInviting(true)}>
              <UserPlus /> Invite
            </Button>
          }
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border text-left text-[11px] font-medium uppercase tracking-wide text-subtle">
              <tr>
                <th className="px-5 py-2">Person</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2">Title / seat</th>
                <th className="px-3 py-2">Budget</th>
                <th className="px-3 py-2">MFA</th>
                <th className="px-3 py-2">Last sign-in</th>
                <th className="w-10 px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.id} className={cn('border-b border-border last:border-0', m.disabled && 'opacity-55')}>
                  <td className="px-5 py-2.5">
                    <div className="flex items-center gap-2.5">
                      <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-surface-3 text-[11px] font-semibold">
                        {initials(m.name)}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate font-medium">
                          {m.name} {m.isSelf && <span className="font-normal text-subtle">(you)</span>}
                          {m.disabled && <Badge className="ml-1.5">Disabled</Badge>}
                        </p>
                        <p className="truncate text-xs text-muted">{m.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2.5">
                    <Select
                      aria-label={`Role for ${m.name}`}
                      className="h-8 w-36 text-[13px]"
                      value={m.role}
                      disabled={m.isSelf || act.isPending}
                      onChange={(e) =>
                        act.mutate({ url: `${base}/members/${m.id}`, method: 'patch', body: { role: e.target.value } })
                      }
                    >
                      {ISSUABLE.map((r) => (
                        <option key={r} value={r}>
                          {ROLE_LABEL[r]}
                        </option>
                      ))}
                    </Select>
                  </td>
                  <td className="px-3 py-2.5 text-[13px]">
                    {m.title ?? <span className="text-subtle">None</span>}
                    {m.seat && <p className="text-xs text-muted">Seat: {m.seat}</p>}
                  </td>
                  <td className="px-3 py-2.5">
                    {m.role === 'ADMIN' ? (
                      <span className="text-xs text-muted">Always</span>
                    ) : m.role === 'EXECUTIVE' ? (
                      <input
                        type="checkbox"
                        aria-label={`Budget access for ${m.name}`}
                        className="size-4 accent-[var(--brand)]"
                        checked={m.budgetAccess}
                        onChange={(e) =>
                          act.mutate({
                            url: `${base}/members/${m.id}`,
                            method: 'patch',
                            body: { budgetAccess: e.target.checked },
                          })
                        }
                      />
                    ) : (
                      <span className="text-xs text-subtle">No</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    {m.mfaEnabled ? (
                      <span className="flex items-center gap-1 text-xs text-ok">
                        <ShieldCheck className="size-3.5" /> On
                      </span>
                    ) : (
                      <span
                        className={cn(
                          'flex items-center gap-1 text-xs',
                          m.role === 'ADMIN' || program.requireMfaForAll ? 'text-warn' : 'text-subtle',
                        )}
                      >
                        <ShieldOff className="size-3.5" /> Off
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-muted tabular">{fmtRelative(m.lastLoginAt)}</td>
                  <td className="px-3 py-2.5">
                    {!m.isSelf && (
                      <RowMenu
                        items={[
                          m.disabled
                            ? {
                                label: 'Enable sign-in',
                                onClick: () => act.mutate({ url: `${base}/members/${m.id}/enable` }),
                              }
                            : {
                                label: 'Disable sign-in',
                                onClick: () => act.mutate({ url: `${base}/members/${m.id}/disable` }),
                              },
                          ...(m.mfaEnabled
                            ? [
                                {
                                  label: 'Reset two-factor',
                                  onClick: () => act.mutate({ url: `${base}/members/${m.id}/reset-mfa` }),
                                },
                              ]
                            : []),
                          {
                            label: 'Remove from program',
                            danger: true,
                            onClick: () =>
                              confirm(`Remove ${m.name} from this program?`) &&
                              act.mutate({ url: `${base}/members/${m.id}/remove` }),
                          },
                        ]}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {invitations.length > 0 && (
        <Card>
          <CardHeader
            title={`Pending invitations (${invitations.length})`}
            description="Links work once and expire after 7 days."
          />
          <ul>
            {invitations.map((inv) => (
              <li
                key={inv.id}
                className="flex items-center gap-3 border-b border-border px-5 py-2.5 text-sm last:border-0"
              >
                <MailCheck className="size-4 text-subtle" />
                <span className="flex-1 truncate">
                  {inv.email} <span className="text-muted">· {ROLE_LABEL[inv.role]}</span>
                  {inv.title && <span className="text-muted"> · {inv.title}</span>}
                </span>
                <span className="text-xs text-muted">Expires {fmtDate(inv.expiresAt)}</span>
                <Button variant="secondary" size="sm" onClick={() => resend(inv)}>
                  New link
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => act.mutate({ url: `${base}/invitations/${inv.id}/revoke` })}
                >
                  Revoke
                </Button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <InviteDialog
        open={inviting}
        program={program}
        onClose={() => setInviting(false)}
        onInvited={(email, url) => {
          setInviting(false);
          refresh();
          if (url) setLink({ email, url });
          else toast.ok(`Invitation emailed to ${email}.`);
        }}
      />
      <Dialog
        open={Boolean(link)}
        onClose={() => setLink(null)}
        title="Share this invitation link"
        description={`Email is not configured, so send this to ${link?.email} yourself. It works once and expires in 7 days.`}
        footer={<Button onClick={() => setLink(null)}>Done</Button>}
      >
        <div className="flex gap-2">
          <Input readOnly value={link?.url ?? ''} className="font-mono text-xs" onFocus={(e) => e.target.select()} />
          <Button
            variant="secondary"
            onClick={() => {
              void navigator.clipboard.writeText(link!.url);
              toast.ok('Link copied.');
            }}
          >
            <Copy /> Copy
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

function InviteDialog({
  open,
  program,
  onClose,
  onInvited,
}: {
  open: boolean;
  program: Program;
  onClose: () => void;
  onInvited: (email: string, url?: string) => void;
}) {
  const base = `/api/programs/${program.id}/admin`;
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('EXECUTIVE');
  const [title, setTitle] = useState('');
  const [budgetAccess, setBudgetAccess] = useState(false);
  const [seatId, setSeatId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const seats = useQuery({
    queryKey: ['admin', program.id, 'seats'],
    queryFn: () => api.get<Seat[]>(`${base}/seats`),
    enabled: open,
  });
  const openSeats = seats.data?.filter((s) => !s.userId) ?? [];

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.post<{ emailed: boolean; inviteUrl?: string }>(`${base}/invitations`, {
        email,
        role,
        title: title || null,
        budgetAccess: role === 'EXECUTIVE' ? budgetAccess : false,
        seatId: role === 'EXECUTIVE' && seatId ? seatId : null,
      });
      onInvited(email, res.inviteUrl);
      setEmail('');
      setTitle('');
      setSeatId('');
      setBudgetAccess(false);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Invite someone"
      description="They get a single-use link to set a password."
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="invite-form" loading={busy}>
            Send invitation
          </Button>
        </>
      }
    >
      <form id="invite-form" onSubmit={submit} className="flex flex-col gap-4">
        {error && <Alert tone="bad">{error}</Alert>}
        <Field label="Email" htmlFor="inv-email">
          <Input
            id="inv-email"
            type="email"
            required
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <Field label="Role" htmlFor="inv-role" hint={ROLE_HINT[role]}>
          <Select id="inv-role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
            {ISSUABLE.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Title" htmlFor="inv-title" hint="Optional, e.g. CFO or Compliance SME.">
          <Input id="inv-title" value={title} onChange={(e) => setTitle(e.target.value)} />
        </Field>
        {role === 'EXECUTIVE' && (
          <>
            <Field
              label="Steering Committee seat"
              htmlFor="inv-seat"
              hint="Links the seat to this person when they accept."
            >
              <Select id="inv-seat" value={seatId} onChange={(e) => setSeatId(e.target.value)}>
                <option value="">No seat</option>
                {openSeats.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.title}
                    {s.holderName ? ` (${s.holderName})` : ''}
                  </option>
                ))}
              </Select>
            </Field>
            <Checkbox
              id="inv-budget"
              checked={budgetAccess}
              onChange={setBudgetAccess}
              label="Budget access"
              hint="Shows the Budget tab. Typically the CFO."
            />
          </>
        )}
      </form>
    </Dialog>
  );
}

function RowMenu({ items }: { items: Array<{ label: string; onClick: () => void; danger?: boolean }> }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative" onBlur={(e) => !e.currentTarget.contains(e.relatedTarget) && setOpen(false)}>
      <Button variant="ghost" size="icon" aria-label="More actions" aria-expanded={open} onClick={() => setOpen(!open)}>
        <MoreHorizontal />
      </Button>
      {open && (
        <div className="absolute right-0 top-9 z-20 w-48 rounded-md border border-border bg-surface p-1 shadow-lg">
          {items.map((it) => (
            <button
              key={it.label}
              className={cn(
                'block w-full rounded px-2.5 py-1.5 text-left text-[13px] hover:bg-surface-2',
                it.danger && 'text-bad',
              )}
              onClick={() => {
                setOpen(false);
                it.onClick();
              }}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
